/**
 * Pure rules of database cells on the client: display text, parsing typed / pasted text into
 * the value the API expects, and which filters / sorting a type offers. The server validates
 * every value again (`app/databases/rules.py`); nothing here is a security check.
 */
import { zonedToInstant } from '@/shared/lib/datetime';

import type { CellValue, DatabaseColumn, FieldOption, FieldType, RecordFilter } from './types';

export const OPTION_TYPES: ReadonlySet<FieldType> = new Set([
  'single_select',
  'status',
  'multi_select',
  'tags',
]);
export const MULTI_OPTION_TYPES: ReadonlySet<FieldType> = new Set(['multi_select', 'tags']);
const SORTABLE: ReadonlySet<FieldType> = new Set([
  'text',
  'long_text',
  'number',
  'checkbox',
  'date',
  'datetime',
  'single_select',
  'status',
]);

const isEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/** `cells` with `changes` applied the way the server applies them: an empty value clears. */
export function applyCells(
  cells: Readonly<Record<string, CellValue>>,
  changes: Readonly<Record<string, unknown>>,
): Record<string, CellValue> {
  const merged: Record<string, unknown> = { ...cells, ...changes };
  return Object.fromEntries(
    Object.entries(merged).filter(([, value]) => !isEmpty(value)),
  ) as Record<string, CellValue>;
}

export const isSortable = (type: FieldType) => SORTABLE.has(type);
export const hasOptions = (type: FieldType) => OPTION_TYPES.has(type);

export interface UserRef {
  user_id: string;
  display_name: string;
  login?: string;
}

export interface CellContext {
  locale: string;
  timeZone: string | undefined;
  users: ReadonlyMap<string, UserRef>;
}

export function optionsOf(column: DatabaseColumn): FieldOption[] {
  return column.config.options ?? [];
}

export function optionById(column: DatabaseColumn, id: string): FieldOption | undefined {
  return optionsOf(column).find((option) => option.id === id);
}

/** A cell value as a list (multi values, multiple users); empty -> `[]`. */
export function asList(value: CellValue | undefined): string[] {
  if (value === undefined || typeof value === 'boolean') return [];
  return Array.isArray(value) ? value : [value];
}

function formatNumber(value: string, decimals: number | undefined, locale: string): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const fraction = decimals ?? value.split('.')[1]?.length ?? 0;
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(number);
  } catch {
    return value;
  }
}

export function formatDate(value: string, locale: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(Date.UTC(year, month - 1, day)),
    );
  } catch {
    return value;
  }
}

export function formatInstant(value: string, locale: string, timeZone?: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timeZone && { timeZone }),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

/** Plain-text form of a cell (display of simple types, copy, record list on phones). */
export function cellText(
  column: DatabaseColumn,
  value: CellValue | undefined,
  context: CellContext,
): string {
  if (value === undefined) return '';
  switch (column.field_type) {
    case 'number':
      return typeof value === 'string'
        ? formatNumber(value, column.config.decimals, context.locale)
        : '';
    case 'checkbox':
      return value === true ? '✓' : '';
    case 'date':
      return typeof value === 'string' ? formatDate(value, context.locale) : '';
    case 'datetime':
      return typeof value === 'string'
        ? formatInstant(value, context.locale, context.timeZone)
        : '';
    case 'single_select':
    case 'status':
    case 'multi_select':
    case 'tags':
      return asList(value)
        .map((id) => optionById(column, id)?.label ?? '')
        .filter(Boolean)
        .join(', ');
    case 'user':
      return asList(value)
        .map((id) => context.users.get(id)?.display_name ?? '')
        .filter(Boolean)
        .join(', ');
    default:
      return typeof value === 'string' ? value : '';
  }
}

/** Text put on the clipboard: canonical where it matters, so pasting it back gives the same value. */
export function copyText(
  column: DatabaseColumn,
  value: CellValue | undefined,
  context: CellContext,
): string {
  if (value === undefined) return '';
  switch (column.field_type) {
    case 'number':
    case 'date':
    case 'datetime':
      return typeof value === 'string' ? value : '';
    case 'checkbox':
      return value === true ? 'true' : 'false';
    default:
      return cellText(column, value, context);
  }
}

/** The wall-clock date and time of an instant in `timeZone` (the date-time editor's fields). */
export function instantParts(
  iso: string,
  timeZone: string | undefined,
): { date: string; time: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      ...(timeZone && { timeZone }),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
    return {
      date: `${get('year')}-${get('month')}-${get('day')}`,
      time: `${get('hour')}:${get('minute')}`,
    };
  } catch {
    return null;
  }
}

/** A wall-clock date + time in `timeZone` -> the instant the API stores (`...Z`). */
export function partsToInstant(
  date: string,
  time: string,
  timeZone: string | undefined,
): string | null {
  const instant = zonedToInstant(date, time || '00:00', timeZone ?? 'UTC');
  return instant ? instant.toISOString().replace('.000Z', 'Z') : null;
}

const TRUE_WORDS = new Set(['true', '1', 'yes', 'y', 'да', 'x', '✓', '+']);
const FALSE_WORDS = new Set(['false', '0', 'no', 'n', 'нет', '-', '']);

/**
 * Text typed into a simple editor or pasted from a spreadsheet -> the API value. `undefined`
 * means "cannot interpret" (the paste is skipped); `null` clears the cell. Option and user cells
 * are matched by label / display name / login, case-insensitively.
 */
export function parseText(
  column: DatabaseColumn,
  raw: string,
  context: Pick<CellContext, 'users' | 'timeZone'>,
): CellValue | null | undefined {
  const text = raw.trim();
  if (text === '' && column.field_type !== 'checkbox') return null;
  switch (column.field_type) {
    case 'text':
      return raw.replace(/\r?\n/g, ' ').trim();
    case 'long_text':
      return raw;
    case 'number': {
      const normalized = text.replace(/\s/g, '').replace(',', '.');
      return /^[+-]?\d+(\.\d+)?$/.test(normalized) ? normalized : undefined;
    }
    case 'checkbox': {
      const word = text.toLocaleLowerCase();
      if (TRUE_WORDS.has(word)) return true;
      return FALSE_WORDS.has(word) ? false : undefined;
    }
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : undefined;
    case 'datetime': {
      if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(text)) {
        const date = new Date(text);
        return Number.isNaN(date.getTime()) ? undefined : date.toISOString().replace('.000Z', 'Z');
      }
      const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(text);
      return match
        ? (partsToInstant(match[1] ?? '', match[2] ?? '', context.timeZone) ?? undefined)
        : undefined;
    }
    case 'single_select':
    case 'status': {
      const option = optionsOf(column).find(
        (o) => o.label.toLocaleLowerCase() === text.toLocaleLowerCase(),
      );
      return option?.id;
    }
    case 'multi_select':
    case 'tags': {
      const ids: string[] = [];
      for (const label of text.split(/[,;]/).map((part) => part.trim().toLocaleLowerCase())) {
        if (!label) continue;
        const option = optionsOf(column).find((o) => o.label.toLocaleLowerCase() === label);
        if (!option) return undefined;
        if (!ids.includes(option.id)) ids.push(option.id);
      }
      return ids;
    }
    case 'user': {
      const ids: string[] = [];
      for (const name of text.split(/[,;]/).map((part) => part.trim().toLocaleLowerCase())) {
        if (!name) continue;
        const user = [...context.users.values()].find(
          (u) =>
            u.display_name.toLocaleLowerCase() === name || u.login?.toLocaleLowerCase() === name,
        );
        if (!user) return undefined;
        if (!ids.includes(user.user_id)) ids.push(user.user_id);
      }
      if (column.config.multiple) return ids;
      return ids.length === 1 ? ids[0] : undefined;
    }
  }
}

/**
 * Filter operators the server accepts per type (`app/databases/query.py`), as OFFERED: only
 * operators that can succeed, in the order a person looks for them. A saved view may still hold an
 * older single-value operator (`is`, `is_not`, `has`); the editor keeps showing it.
 */
export function filterOperators(type: FieldType, multiple = false): string[] {
  const always = ['empty', 'not_empty'];
  switch (type) {
    case 'text':
    case 'long_text':
      return ['contains', 'not_contains', 'eq', 'neq', 'starts', ...always];
    case 'number':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...always];
    case 'checkbox':
      return ['is'];
    case 'date':
    case 'datetime':
      return ['on', 'before', 'after', 'between', ...always];
    case 'single_select':
      return ['in', 'not_in', ...always];
    case 'status':
      return ['in', 'not_in', 'category', ...always];
    case 'multi_select':
    case 'tags':
      return ['has_any', 'has_all', 'has_none', ...always];
    case 'user':
      return multiple
        ? ['has_any', 'has_all', 'has_none', ...always]
        : ['has_any', 'has_none', ...always];
  }
}

/** Conditions whose value is a list of ids (one box of checkboxes). */
export const LIST_OPERATORS: ReadonlySet<string> = new Set([
  'in',
  'not_in',
  'has_any',
  'has_all',
  'has_none',
]);
/** Conditions with two values, a low and a high one. */
export const PAIR_OPERATORS: ReadonlySet<string> = new Set(['between']);

/** The parts of a stored value: ids of a list condition, the two ends of a range. */
export const valueParts = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
export const joinParts = (parts: readonly string[]): string => parts.join(',');

export const operatorNeedsValue = (op: string) => op !== 'empty' && op !== 'not_empty';

/** A filter is complete enough to send. */
export function isCompleteFilter(filter: RecordFilter): boolean {
  if (!operatorNeedsValue(filter.op)) return true;
  const parts = valueParts(filter.value);
  if (PAIR_OPERATORS.has(filter.op)) return parts.length === 2;
  return LIST_OPERATORS.has(filter.op) ? parts.length > 0 : (filter.value ?? '') !== '';
}
